import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check, Mail, MapPin, Phone, Linkedin } from "lucide-react";
import { COMPANY } from "@/data/content";
import { Reveal, MaskedLines, Eyebrow, EASE } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INITIAL = { name: "", email: "", company: "", brand: "malansh", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(INITIAL);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setDone(true);
      setForm(INITIAL);
      toast.success("Enquiry received. We'll reply within one business day.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail[0]?.msg : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative overflow-hidden bg-brand-navy py-28 text-white lg:py-40">
      <div className="absolute -left-40 bottom-0 h-[500px] w-[500px] rounded-full bg-brand-indigo/40 blur-[150px]" />
      <div className="relative mx-auto grid max-w-[1400px] gap-16 px-6 lg:grid-cols-12 lg:px-10">
        <div className="lg:col-span-5">
          <Reveal><Eyebrow n="07" label="Contact" light /></Reveal>
          <MaskedLines
            as="h2"
            inView
            delay={0.1}
            className="mt-6 font-display text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl"
            lines={["Let's talk about", "your back office."]}
          />
          <Reveal delay={0.3}>
            <p className="mt-8 max-w-md text-base leading-relaxed text-white/60 md:text-lg">
              Tell us which brand you're interested in and what you'd like to hand off. We'll come back with a scoped plan and a discovery call.
            </p>
          </Reveal>
          <Reveal delay={0.4} className="mt-12 space-y-5 font-body text-sm">
            <a data-testid="contact-email" href={`mailto:${COMPANY.email}`} className="flex items-center gap-4 text-white/75 transition-colors hover:text-brand-cyan">
              <Mail className="h-4 w-4 text-brand-cyan" /><span className="link-underline">{COMPANY.email}</span>
            </a>
            <a data-testid="contact-phone" href={COMPANY.phoneHref} className="flex items-center gap-4 text-white/75 transition-colors hover:text-brand-cyan">
              <Phone className="h-4 w-4 text-brand-cyan" /><span className="link-underline">{COMPANY.phone}</span>
            </a>
            {COMPANY.linkedin && (
              <a data-testid="contact-linkedin" href={COMPANY.linkedin} target="_blank" rel="noreferrer" className="flex items-center gap-4 text-white/75 transition-colors hover:text-brand-cyan">
                <Linkedin className="h-4 w-4 text-brand-cyan" /><span className="link-underline">LinkedIn</span>
              </a>
            )}
            <div data-testid="contact-address" className="flex items-start gap-4 text-white/75">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand-cyan" /><span className="max-w-xs leading-relaxed">{COMPANY.address}</span>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.2} className="lg:col-span-7">
          <div className="glass relative rounded-[2rem] p-6 sm:p-10">
            <AnimatePresence mode="wait">
              {done ? (
                <motion.div
                  key="ok"
                  data-testid="enquiry-success-message"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.7, ease: EASE }}
                  className="flex min-h-[420px] flex-col items-start justify-center"
                >
                  <span className="grid h-14 w-14 place-items-center rounded-full bg-brand-cyan text-brand-navy"><Check className="h-6 w-6" /></span>
                  <h3 className="mt-8 font-display text-3xl font-bold tracking-tight">Thank you — we've got it.</h3>
                  <p className="mt-4 max-w-md text-white/60">Your enquiry is saved and someone from the Malansh team will reply within one business day.</p>
                  <button data-testid="enquiry-send-another" onClick={() => setDone(false)} className="btn-ghost mt-10"><span>Send another</span></button>
                </motion.div>
              ) : (
                <motion.form key="form" data-testid="contact-form" onSubmit={submit} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="grid gap-5 sm:grid-cols-2">
                  <label className="block">
                    <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">Full name *</span>
                    <input data-testid="contact-form-name-input" required minLength={2} value={form.name} onChange={set("name")} className="field" placeholder="Jane Doe" />
                  </label>
                  <label className="block">
                    <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">Work email *</span>
                    <input data-testid="contact-form-email-input" required type="email" value={form.email} onChange={set("email")} className="field" placeholder="jane@company.com" />
                  </label>
                  <label className="block">
                    <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">Company</span>
                    <input data-testid="contact-form-company-input" value={form.company} onChange={set("company")} className="field" placeholder="Company / Practice name" />
                  </label>
                  <label className="block">
                    <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">Interested in *</span>
                    <select data-testid="contact-form-brand-select" value={form.brand} onChange={set("brand")} className="field appearance-none [&>option]:text-brand-ink">
                      <option value="malansh">Malansh Solutions (general)</option>
                      <option value="artwork360">Artwork 360 — Creative & orders</option>
                      <option value="medlyn360">Medlyn 360 — Medical scribing</option>
                      <option value="other">Something else</option>
                    </select>
                  </label>
                  <label className="block sm:col-span-2">
                    <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">Message *</span>
                    <textarea data-testid="contact-form-message-input" required minLength={10} rows={5} value={form.message} onChange={set("message")} className="field resize-none" placeholder="What would you like to hand off, and what does success look like?" />
                  </label>
                  <div className="flex flex-wrap items-center justify-between gap-4 sm:col-span-2">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/35">Please don't include patient information.</p>
                    <button data-testid="contact-form-submit-button" type="submit" disabled={busy} className="btn-primary disabled:opacity-60">
                      <span>{busy ? "Sending…" : "Send enquiry"}</span>
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
